import React from 'react';
import { Heading, Alert, AlertIcon, AlertTitle, AlertDescription, CloseButton } from '@chakra-ui/react';
import sortBy from 'lodash/sortBy';

import NewTicketForm from 'src/NewTicketForm';
import CustomerEntityInfo from 'src/CustomerEntityInfo';
import TicketList from 'src/TicketList';
import BackendApi from 'src/api/BackendApi';
import FeatureFlags from 'src/api/FeatureFlags';
import { getUrlSearchParam } from 'src/utils';
import type Ticket from 'src/types/Ticket';
import type { TicketTypeEntity } from 'src/CustomerEntityInfo';

interface HashIdentityProps {}

interface HashIdentityState {
  tickets: Ticket[];
  isLoading: boolean;
  users: { [key: string]: any }[];
  customerData: Record<string, string>;
  ticketTypeEntity: TicketTypeEntity[];
  contentLoaded: boolean;
  noHash: boolean;
  page: 'tickets' | 'entity' | 'new';
  error: boolean;
  selectedTicket: string | null;
}

export default class HashIdentity extends React.Component<HashIdentityProps, HashIdentityState> {
  constructor(props: HashIdentityProps) {
    super(props);

    this.state = {
      tickets: [],
      isLoading: false,
      users: [],
      customerData: {},
      ticketTypeEntity: [],
      contentLoaded: false,
      page: 'tickets',
      noHash: false,
      error: false,
      selectedTicket: null
    };
  }

  async componentDidMount() {
    const isEID = FeatureFlags.isFlagOn('ENABLE_PORTAL_EID_AUTH');

    if (!isEID) {
      const query = window.location.search;
      const urlParams = new URLSearchParams(query);
      const hash = getUrlSearchParam('hash');
      const ticketId = urlParams.get('ticketId');
      if (!hash) {
        this.setState({ noHash: true });
      } else {
        this.handleEntitiesFetch(hash, ticketId);
      }
    } else {
      this.handleEntitiesFetch();
    }
  }

  private handleEntitiesFetch = (hash?: string, ticketId?: string | null) => {
    this.setState({ isLoading: true, noHash: false }, async () => {
      try {
        const ticketResponse = await BackendApi.getTicketByHash(hash);
        const entityResponse = await BackendApi.getEntityByHash({ hash });

        this.setState(
          {
            tickets: sortBy(ticketResponse.tickets, (x) => x.id).reverse(),
            ticketTypeEntity: ticketResponse.entity || [],
            customerData: entityResponse,
            selectedTicket: ticketId || null
          },
          () => {
            this.setState(
              {
                isLoading: false,
                contentLoaded: true,
                noHash: false
              },
              () => {
                if (this.state.selectedTicket) {
                  const elmnt = document.getElementById(this.state.selectedTicket);
                  elmnt?.scrollIntoView();
                }
              }
            );
          }
        );
      } catch (error) {
        console.error(error);
        this.setState({ error: true });
      }
    });
  };

  private fetchTickets = async () => {
    const query = window.location.search;
    const urlParams = new URLSearchParams(query);
    const hash = urlParams.get('hash');
    const ticketResponse = await BackendApi.getTicketByHash(hash as string);
    this.setState({ tickets: ticketResponse.tickets, isLoading: false });
  };

  private displayError = () => {
    if (this.state.error) {
      return (
        <div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
          <Alert status="error" borderRadius={8}>
            <AlertIcon />
            <AlertTitle mr={2}> On tapahtunut virhe. </AlertTitle>
            <AlertDescription>Päivitä sivu tai ota yhteyttä asiakaspalveluun.</AlertDescription>
            <CloseButton position="absolute" right="8px" top="8px" />
          </Alert>
        </div>
      );
    }
    return null;
  };

  private displayPage() {
    switch (this.state.page) {
      case 'entity':
        return (
          <CustomerEntityInfo
            isLoaded={this.state.contentLoaded}
            customerData={this.state.customerData}
            ticketTypeEntity={this.state.ticketTypeEntity}
          />
        );
      case 'new':
        return (
          <NewTicketForm
            callbackFormIsDone={() => {
              this.setState({ page: 'tickets', isLoading: true });
              setTimeout(() => {
                this.fetchTickets();
              }, 1000);
              setTimeout(() => {
                this.fetchTickets();
              }, 5000);
            }}
            customerData={this.state.customerData}
            fetchTickets={this.fetchTickets}
          />
        );
      case 'tickets':
        return (
          <TicketList
            isLoaded={this.state.contentLoaded}
            tickets={this.state.tickets}
            selectedTicket={this.state.selectedTicket}
            fetchTickets={this.fetchTickets}
          />
        );
    }
  }

  render() {
    const { noHash, error } = this.state;

    return (
      <>
        {this.displayError()}

        {noHash === false && !error && (
          <div className="App">
            <div className="formContainer">
              <div style={{ display: 'flex', verticalAlign: 'middle', textAlign: 'center', marginBottom: '20px' }}>
                <img
                  style={{ display: 'inline' }}
                  width={125}
                  height={64}
                  src={`${process.env.PUBLIC_URL}/espoo.png`}
                />
                <span style={{ fontSize: '20px', paddingTop: '30px' }}>Asiointiportaali</span>
              </div>

              <div style={{ display: 'flex', marginLeft: '50px', marginRight: '50px' }}>
                <div style={{ width: '20%', paddingTop: '20px' }}>
                  <div className="portalNewTicketHeading link">
                    <Heading as="h4" size="md" className="link" onClick={() => this.setState({ page: 'tickets' })}>
                      Omat palvelupyynnöt
                    </Heading>
                  </div>
                  <div className="portalNewTicketHeading link">
                    <Heading as="h4" size="md" className="link" onClick={() => this.setState({ page: 'entity' })}>
                      Omat tiedot
                    </Heading>
                  </div>
                  <div className="portalNewTicketHeading link">
                    <Heading as="h4" size="md" className="link" onClick={() => this.setState({ page: 'new' })}>
                      Uusi palvelupyyntö +
                    </Heading>
                  </div>
                </div>
                <div style={{ width: '50px' }}></div>
                <div style={{ width: '80%', overflowY: 'scroll' }}>{this.displayPage()}</div>
              </div>
            </div>
          </div>
        )}
        {noHash && (
          <div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
            <Alert status="error" borderRadius={8}>
              <AlertIcon />
              <AlertTitle mr={2}>Puuttuva tunniste </AlertTitle>
              <AlertDescription>Tarkista sähköpostisi linkki.</AlertDescription>
              <CloseButton position="absolute" right="8px" top="8px" />
            </Alert>
          </div>
        )}
      </>
    );
  }
}
